if (!customElements.get('ws-footer-accordion')) {
  class WsFooterAccordionItem {
    constructor(details, mobileQuery, reducedMotionQuery) {
      this.details = details;
      this.summary = details.querySelector('summary');
      this.content = details.querySelector('.accordion__content');
      this.mobileQuery = mobileQuery;
      this.reducedMotionQuery = reducedMotionQuery;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;

      if (!this.summary || !this.content) return;
      this.summary.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      // Always prevent the native instant toggle, even at >=750px: pointer-events:none
      // (set in ws-footer.liquid) already blocks mouse there, but a focused <summary> is
      // still keyboard-activatable (Tab, then Enter/Space), which pointer-events can't stop.
      event.preventDefault();
      if (!this.mobileQuery.matches) return;

      const willOpen = this.isClosing || !this.details.open;
      this.details.classList.toggle('is-open', willOpen);
      this.details.style.overflow = 'hidden';
      if (willOpen) {
        this.open();
      } else {
        this.shrink();
      }
    }

    duration() {
      return this.reducedMotionQuery.matches ? 1 : 300;
    }

    shrink() {
      this.isClosing = true;
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;
      this.animation?.cancel();
      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: this.duration(), easing: 'ease-out' }
      );
      this.animation.onfinish = () => this.onFinish(false);
      this.animation.oncancel = () => {
        this.isClosing = false;
      };
    }

    open() {
      this.details.style.height = `${this.details.offsetHeight}px`;
      this.details.open = true;
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      this.isExpanding = true;
      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;
      this.animation?.cancel();
      this.animation = this.details.animate(
        { height: [startHeight, endHeight] },
        { duration: this.duration(), easing: 'ease-out' }
      );
      this.animation.onfinish = () => this.onFinish(true);
      this.animation.oncancel = () => {
        this.isExpanding = false;
      };
    }

    onFinish(open) {
      this.details.open = open;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.details.style.height = '';
      this.details.style.overflow = '';
    }
  }

  class WsFooterAccordion extends HTMLElement {
    connectedCallback() {
      // Markup ships with every <details open> by default (see ws-footer.liquid) so the
      // >=750px layout needs zero JS to show its links. This custom element's only job at
      // that breakpoint is staying out of the way; below 750px it collapses them on connect.
      this.details = Array.from(this.querySelectorAll('details'));
      this.mediaQuery = window.matchMedia('(max-width: 749px)');
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.items = this.details.map(
        (detail) => new WsFooterAccordionItem(detail, this.mediaQuery, reducedMotionQuery)
      );
      this.sync = this.sync.bind(this);
      this.sync();
      this.mediaQuery.addEventListener('change', this.sync);
      this.focusPostedNewsletter();
    }

    // Com ?customer_posted=true a Shopify marca sucesso ou erro em todo form
    // 'customer' da página (o ws-countdown também tem um). Só o form do
    // #<id> devolvido pela Shopify recebe foco e aria; antes era autofocus
    // no Liquid, que valia pra qualquer envio da página. Pelo location.hash
    // e não por :target, que ainda não vale quando os scripts defer rodam.
    focusPostedNewsletter() {
      const id = decodeURIComponent(location.hash.slice(1));
      let form = id && this.querySelector(`form.footer__newsletter[id="${CSS.escape(id)}"]`);
      if (!form) {
        // Nenhum formulário da página é o do #id (a volta de um desafio do
        // captcha pode chegar sem ele): a confirmação fica aqui, como no
        // footer do Dawn, em vez de não aparecer em lugar nenhum.
        const claimed = id && document.querySelector(`form[id="${CSS.escape(id)}"]`);
        if (!claimed) form = this.querySelector('form.footer__newsletter');
        if (!form || !form.querySelector('.newsletter-form__message')) return;
      }
      // Mostra a mensagem já: o CSS do :target só vale depois, e elemento
      // escondido não recebe foco.
      form.classList.add('ws-footer__newsletter--target');
      const input = form.querySelector('input[type="email"]');
      const success = form.querySelector('.newsletter-form__message--success');
      const error = form.querySelector('.newsletter-form__message:not(.newsletter-form__message--success)');
      let target = null;
      if (error) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', error.id);
        target = input;
      } else if (success) {
        input.setAttribute('aria-describedby', success.id);
        target = success;
      }
      if (!target) return;
      // Foco só depois do load: ao rolar até o #id da URL o Chrome limpa o
      // foco, e um focus() feito antes disso se perde.
      if (document.readyState === 'complete') target.focus();
      else window.addEventListener('load', () => target.focus(), { once: true });
    }

    disconnectedCallback() {
      this.mediaQuery.removeEventListener('change', this.sync);
    }

    sync() {
      const collapse = this.mediaQuery.matches;
      this.details.forEach((detail) => {
        detail.open = !collapse;
        detail.classList.toggle('is-open', !collapse);
      });
    }
  }

  customElements.define('ws-footer-accordion', WsFooterAccordion);
}
